import { isPolygon } from '../../shared/geometry';
import type { Figure } from '../../shared/math';
import type { PolygonKind, ShapeKind } from '../../shared/protocol';
import { createStore } from './store';

export type Tool = 'pan' | 'pen' | 'highlighter' | 'eraser' | ShapeKind | 'text' | 'laser' | 'figure';
export type SizeGroup = 'pen' | 'highlighter' | 'shape' | 'text' | 'eraser';
export type ColorGroup = 'pen' | 'highlighter' | 'shape' | 'text';

/** Sayfa birimiyle (punto) kalınlıklar: ince / orta / kalın. */
export const SIZES: Record<SizeGroup, [number, number, number]> = {
  pen: [1.6, 2.8, 5],
  highlighter: [9, 15, 24],
  shape: [1.5, 2.5, 4.5],
  text: [14, 20, 28],
  eraser: [5, 10, 20],
};

export const PALETTE = ['#0A105E', '#111827', '#2563EB', '#DC2626', '#16A34A', '#EA580C', '#6348CB'];
export const HIGHLIGHTS = ['#FDE047', '#86EFAC', '#93C5FD', '#F9A8D4', '#FDBA74'];

export function sizeGroup(tool: Tool): SizeGroup | null {
  switch (tool) {
    case 'pen':
      return 'pen';
    case 'highlighter':
      return 'highlighter';
    case 'text':
      return 'text';
    case 'eraser':
      return 'eraser';
    case 'pan':
    case 'laser':
    case 'figure': // çizgi kalınlığı formül oluşturulurken belirlenir
      return null;
    default:
      return 'shape';
  }
}

export const isPolygonTool = (tool: Tool): tool is PolygonKind => tool !== 'pan' && tool !== 'pen' && tool !== 'highlighter' && tool !== 'eraser' && tool !== 'text' && tool !== 'laser' && tool !== 'figure' && isPolygon(tool);

export function colorGroup(tool: Tool): ColorGroup | null {
  if (tool === 'figure') return 'shape';
  const g = sizeGroup(tool);
  return g === 'eraser' ? null : g;
}

export interface ToolState {
  tool: Tool;
  /** Şekil menüsünün düğmesinde gösterilen ve G tuşunun seçtiği son çokgen. */
  polygon: PolygonKind;
  color: Record<ColorGroup, string>;
  size: Record<SizeGroup, 0 | 1 | 2>;
}

const DEFAULTS: ToolState = {
  tool: 'pen',
  polygon: 'triangle',
  color: { pen: '#2563EB', highlighter: '#FDE047', shape: '#DC2626', text: '#111827' },
  size: { pen: 1, highlighter: 1, shape: 1, text: 1, eraser: 1 },
};

const KEY = 'nova.tools.v1';

function load(): ToolState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (raw && typeof raw === 'object') {
      // Yerleştirilmeyi bekleyen formül sayfa yenilenince kaybolur; araç da kalmamalı.
      return { tool: raw.tool && raw.tool !== 'figure' ? raw.tool : DEFAULTS.tool, polygon: isPolygon(raw.polygon) ? raw.polygon : DEFAULTS.polygon, color: { ...DEFAULTS.color, ...raw.color }, size: { ...DEFAULTS.size, ...raw.size } };
    }
  } catch {
    // depolama kapalı olabilir
  }
  return DEFAULTS;
}

export const toolStore = createStore<ToolState>(load());
toolStore.subscribe(() => {
  try {
    localStorage.setItem(KEY, JSON.stringify(toolStore.get()));
  } catch {
    // yok say
  }
});

export const setTool = (tool: Tool) => toolStore.set(isPolygonTool(tool) ? { tool, polygon: tool } : { tool });

/** Matematik panelinin sayfayla paylaştığı geçici durum; kalıcı değildir. */
export const mathStore = createStore<{
  /** Sayfaya dokununca yerleştirilecek formül. */
  figure: Figure | null;
  /** Sayfaya dokununca açılan metin kutusuna eklenecek işaret. */
  pendingSymbol: string | null;
  /** Metin kutusu açıksa imlecin olduğu yere yazar. */
  insert: ((text: string) => void) | null;
}>({ figure: null, pendingSymbol: null, insert: null });
